import React from "react";
import * as styles from "./ContactsPanel.styles.ts";
import { Flex } from "src/components/Flex.tsx";
import { IconButtonV2 } from "src/components/IconButtonV2/IconButtonV2.tsx";
import {
  useFormContext,
  useFieldArray,
  Controller,
  get,
} from "react-hook-form";
import { TextField } from "src/components/Form/fields/TextField.tsx";
import { SingleSelectField } from "src/components/Form/fields/SingleSelectField.tsx";
import { Panel } from "src/components/Panel/Panel.tsx";

import { TranslationNamespaceEnum } from "src/lib/i18next/types.ts";
import { useTranslation } from "react-i18next";
import { useContactTypes } from "src/components/Form/panels/ContactsPanel/useContactTypes.ts";
import { loadTranslations } from "src/lib/i18next/loadTranslations.ts";
import { EmailContactType, PhoneContactType } from "@exonn/js-sdk";
import { LinkButton } from "src/components/LinkButton/LinkButton.tsx";

loadTranslations(TranslationNamespaceEnum.FormContactsPanel);

export const ContactsPanel: React.FC = () => {
  const form = useFormContext();

  const { t } = useTranslation(TranslationNamespaceEnum.FormContactsPanel);

  const { emailTypes, phoneTypes } = useContactTypes();

  const {
    fields: phonesFields,
    append: appendPhone,
    update: updatePhone,
    remove: removePhone,
  } = useFieldArray({
    control: form.control,
    name: "phones",
  });

  const {
    fields: emailsFields,
    append: appendEmail,
    update: updateEmail,
    remove: removeEmail,
  } = useFieldArray({
    control: form.control,
    name: "emails",
  });

  return (
    <Panel>
      <Panel.Header>
        <Panel.Title>{t("panel.contacts.title")}</Panel.Title>
      </Panel.Header>

      <Panel.Body>
        {/* Phones */}
        <div css={styles.section}>
          {phonesFields.map((field, index) => (
            <Flex css={styles.row} key={field.id}>
              <div css={styles.fixedWidthSelect}>
                <SingleSelectField
                  name={`phones.${index}.contact_subtype`}
                  label={t("entity.phoneType")}
                  control={form.control}
                  options={phoneTypes}
                  defaultValue={PhoneContactType.Landline}
                  allowClearValue={false}
                  showSearch={false}
                />
              </div>
              <div css={styles.inputGrow}>
                <TextField
                  name={`phones.${index}.contact_value`}
                  error={
                    get(
                      form.formState.errors?.root || {},
                      `phones.${index}.contact_value`,
                    )?.message // server error
                  }
                  label={t("entity.phone")}
                  control={form.control}
                  placeholder="+1234567890"
                  type="tel"
                />
              </div>
              <Controller
                name={`phones.${index}`}
                control={form.control}
                render={({ field: { value } }) => (
                  <IconButtonV2
                    skin="danger"
                    variant="outlined"
                    size="sm"
                    disabled={
                      phonesFields.length === 1 && value.contact_value === ""
                    }
                    style={{ padding: "5px 10px", marginTop: 21 }}
                    onClick={() => {
                      if (phonesFields.length === 1) {
                        // don't delete last phone row, just clear input value
                        updatePhone(index, {
                          contact_subtype: PhoneContactType.Landline,
                          contact_type: "phone",
                          contact_value: "",
                        });
                      } else {
                        removePhone(index);
                      }
                    }}
                  >
                    <i
                      style={{ display: "flex" }}
                      className={"icon-15-trash"}
                    />
                  </IconButtonV2>
                )}
              />
            </Flex>
          ))}
          <div css={styles.sectionFooter}>
            <LinkButton
              onClick={(e) => {
                e.preventDefault();
                appendPhone({
                  contact_subtype: PhoneContactType.Landline,
                  contact_value: "",
                  contact_type: "phone",
                });
              }}
            >
              {t("addPhone")}
            </LinkButton>
          </div>
        </div>

        {/* Emails */}
        <div css={styles.section}>
          {emailsFields.map((field, index) => (
            <Flex css={styles.row} key={field.id}>
              <div css={styles.fixedWidthSelect}>
                <SingleSelectField
                  name={`emails.${index}.contact_subtype`}
                  label={t("entity.emailType")}
                  control={form.control}
                  options={emailTypes}
                  defaultValue={EmailContactType.Standard}
                  allowClearValue={false}
                  showSearch={false}
                />
              </div>
              <div css={styles.inputGrow}>
                <TextField
                  name={`emails.${index}.contact_value`}
                  error={
                    get(
                      form.formState.errors?.root || {},
                      `emails.${index}.contact_value`,
                    )?.message
                  }
                  label={t("entity.email")}
                  control={form.control}
                  placeholder="company@com.com"
                  type="email"
                />
              </div>
              <Controller
                name={`emails.${index}`}
                control={form.control}
                render={({ field: { value } }) => (
                  <IconButtonV2
                    skin="danger"
                    variant="outlined"
                    size="sm"
                    disabled={
                      emailsFields.length === 1 && value.contact_value === ""
                    }
                    style={{ padding: "5px 10px", marginTop: 21 }}
                    onClick={() => {
                      if (emailsFields.length === 1) {
                        // don't delete last email row, just clear input value
                        updateEmail(index, {
                          contact_subtype: EmailContactType.Standard,
                          contact_type: "email",
                          contact_value: "",
                        });
                      } else {
                        removeEmail(index);
                      }
                    }}
                  >
                    <i
                      style={{ display: "flex" }}
                      className={"icon-15-trash"}
                    />
                  </IconButtonV2>
                )}
              />
            </Flex>
          ))}
          <div css={styles.sectionFooter}>
            <LinkButton
              onClick={(e) => {
                e.preventDefault();
                appendEmail({
                  contact_subtype: EmailContactType.Standard,
                  contact_value: "",
                  contact_type: "email",
                });
              }}
            >
              {t("addEmail")}
            </LinkButton>
          </div>
        </div>
      </Panel.Body>
    </Panel>
  );
};
